import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Por favor ingresa tu email");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });
      setSent(true);
    } catch (error) {
      console.error("Error requesting password reset:", error);
      // Always show success message to not reveal if email exists
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md">
        {/* Back to login */}
        <Link to="/login" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Volver a iniciar sesión</span>
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              {sent ? (
                <CheckCircle className="h-6 w-6 text-emerald-500" />
              ) : (
                <Mail className="h-6 w-6 text-primary" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {sent ? "¡Revisa tu correo!" : "¿Olvidaste tu contraseña?"}
            </CardTitle>
            <CardDescription>
              {sent 
                ? "Si el correo existe en nuestro sistema, recibirás instrucciones para restablecer tu contraseña."
                : "Ingresa tu email y te enviaremos instrucciones para restablecer tu contraseña."
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {sent ? (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg text-sm text-center text-muted-foreground">
                  <p>El enlace expira en 1 hora.</p>
                  <p className="mt-2">¿No lo ves? Revisa tu carpeta de spam.</p>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSent(false)}
                >
                  Enviar de nuevo
                </Button>
                
                <Link to="/login">
                  <Button className="w-full">
                    Volver a iniciar sesión
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      autoFocus
                      required
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</>
                  ) : (
                    "Enviar instrucciones"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
